import { clientAPISend } from '@1x2network/client_api_integration'
import { slamStopHandler } from '@1x2network/duck_slot_game'
import { flow } from '@1x2network/flow'
import { isPaylineConfiguration } from '@1x2network/is_engine_communication'
import { Specification, Spine } from '@1x2network/pixi_assets'
import { MediumWinPopupSpec } from '@1x2network/pixi_dialogue'
import { slotStore } from '@1x2network/slot-ui'
import { State } from '@1x2network/state_controller'
import { produce } from 'immer'
import { SignalConnection } from 'typed-signals'
import { valueIds } from '../../constants/valueIds'
import { AnyBaseGameModel, BaseGameToHoldAndWinModel, CoinShowerEvent, isBonusTrigger } from '../../model/BaseGameModel'
import { CollectionEvent, HoldAndWinToBaseModel } from '../../model/HoldAndWinModel'
import { SetupModel } from '../../model/SetupModel'
import { SpinModel } from '../../model/SpinModel'
import { applyHoldAndWinTransition } from '../../utils/applyBoardTransforms'
import { applyFinalValues } from '../../utils/applyFinalValues'
import { formatCurrency } from '../../utils/formatCurrency'
import { sortByRowThenReel } from '../../utils/positionTuple'
import { GameViews } from '../../views/GameViews'
import { initWinLines } from './initWinLines'

/**
 * Game loop logic for the base state of the game
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class BaseState extends State<GameViews, AnyBaseGameModel | HoldAndWinToBaseModel, SetupModel> {
    private isLoopingWinlines = false

    public prepareState(model: AnyBaseGameModel | HoldAndWinToBaseModel | SetupModel): void {
        slotStore.getState().slamStop.setEnabled(true)

        const { middleReel, hawReelSet, hwBackground, baseBackground, excitement, reelSet } = this.views
        baseBackground.visible = true
        hwBackground.visible = false
        hawReelSet.visible = false
        hawReelSet.getTopContainer(0).visible = false
        hawReelSet.getTopContainer(1).visible = false
        hawReelSet.getTopContainer(2).visible = false
        this.views.coinShower.visible = true

        excitement.setState('base')
        excitement.startIdle()
        middleReel.hide()
        reelSet.addChild(middleReel.backgroundSpine, middleReel.foregroundSpine)

        reelSet.visible = true
        reelSet.topContainer.visible = true
        baseBackground.reset()
        baseBackground.visible = true
        baseBackground.playIdle()
    }

    public handshake(model: SetupModel): void {
        if ('results' in model) {
            const { results } = model
            const { reelSet } = this.views

            const mutatedSymbols = applyFinalValues(results.board.reels)

            // Prepare with value symbols, might be restarting with transition data
            reelSet.prepareSpin(mutatedSymbols)

            if (isPaylineConfiguration(model.config)) {
                initWinLines(reelSet, this.views.winLines, model.config.lines)
            }
        }

        // Update jackpot widget values
        this.views.jackpotWidgets.updateValues(model.config.prizes)
    }

    public async introduceState(model: SetupModel): Promise<void> {
        slotStore.getState().console.setIdle()
        this.views.falsePersistence.setInitialBase()
        await flow.wait(400)
        this.views.idleHandler.setIdle()
    }

    public async waitForInput(interact: Promise<void>): Promise<object | void> {
        // dont show idle animations if we are looping winlines
        if (!this.isLoopingWinlines) {
            this.views.idleHandler.setIdle()
        }
        slotStore.getState().console.setIdle()
        this.views.excitement.startIdle()
        slamStopHandler.reset()
        await interact
        this.views.jackpotWidgets.reset()
        slotStore.getState().message.clearMessage()
        this.views.excitement.startHype()
        this.views.winLines.stopIteration()
        this.views.idleHandler.setActive()
        this.isLoopingWinlines = false
    }

    public async waitForRound() {
        slotStore.getState().console.setSpinning()
        await this.views.reelSet.startSpin()
    }

    public inState(model: SpinModel | SetupModel): boolean {
        return (
            model.state.phase.next === 'NORMAL' ||
            (model.state.phase.current === 'NORMAL' && model.state.phase.next === '')
        )
    }

    public async leaveState(model: BaseGameToHoldAndWinModel): Promise<void> {
        const { soundPlayer } = this.views
        soundPlayer.fadeBackground(0.5)
        flow.wait(3400).then(() => soundPlayer.fadeBackground(1))
        soundPlayer.playHawStart()
        soundPlayer.playTransitionIn()
        slotStore.getState().spins.updateSpins(0)
        slotStore.getState().slamStop.setEnabled(false)
        await this.views.transition.enterHoldAndWin()
        this.views.falsePersistence.setInitialBonus()

        const reels = model.results.board.reels
        const transformEvent = model.results.wins.events?.find((event) => event.type === 'TRANSFORM')
        const mutatedSymbols = applyHoldAndWinTransition(reels, transformEvent)
        this.views.hawReelSet.prepareSpin(mutatedSymbols)
    }

    public async enterState(model: HoldAndWinToBaseModel): Promise<void> {
        const { soundPlayer } = this.views

        // show footer message of total win amount after bonus
        if (model.state.win.total.currency) {
            slotStore.getState().message.setMessage('returns', [formatCurrency(model.state.win.total.currency)])
        }

        this.views.falsePersistence.reset()
        await flow.wait(200)
        this.views.soundPlayer.playTransitionOut()
        await this.views.transition.exitHoldAndWin()
        slotStore.getState().console.setIdle()
        soundPlayer.fadeBackground(1)
        clientAPISend.feature.end()
    }

    public async playRound(model: AnyBaseGameModel): Promise<void> {
        const { reelSet, eventManager, falsePersistence } = this.views
        eventManager.setEvents(model.results.wins.events)

        /** handle a coin shower in the game */
        const isTransforming = eventManager.peek()?.type === 'TRANSFORM'
        const transformEvent = eventManager.peek() as CoinShowerEvent

        // Handle the collection of symbols for false persistence
        const landConnection = reelSet.spinAnimator.signals.onLand.connect((index) => {
            const symbols = reelSet.fetcher.getAllSymbolsByReel(index)
            const triggerSymbols = symbols.filter((s) => valueIds.includes(s.id))
            if (!isTransforming) {
                triggerSymbols.forEach((symbol) => falsePersistence.collect(symbol))
            } else {
                for (const triggerSymbol of triggerSymbols) {
                    triggerSymbol.playLanding()
                }
            }
            this.views.jackpotWidgets.displayLanded(symbols.map((s) => s.id))
        })

        const spinInData = produce(model.results.board.reels, (draft) => {
            if (!isTransforming) {
                return
            }

            for (const transform of transformEvent.transforms) {
                const [reel, row] = transform.position
                draft[reel][row] = { id: -1 }
            }
        })

        if (isTransforming) {
            flow.wait(500).then(() => falsePersistence.countUpTo(falsePersistence.maxTier - 1))
            await this.handleCoinShower(eventManager.nextEvent() as CoinShowerEvent)
        } else if (!slamStopHandler.isSlamStopping) {
            this.views.anticipationManager.initConnections(model)
        }

        const anticipate = this.views.anticipationManager.calculateAnticipation(model)
        const coinStagger = this.views.anticipationManager.calculateCoinStagger(model)
        const hasAnticipated = anticipate.some((a) => a > 0)
        const totalAnticipation = anticipate.map((a, i) => a + coinStagger[i])

        let connection: SignalConnection | undefined
        if (!isTransforming && slamStopHandler.canSlamStop) {
            if (slamStopHandler.isSlamStopping) {
                flow.skipStart()
            } else {
                connection = slamStopHandler.onSlamStop.connect(() => flow.skipStart())
            }
        }

        await reelSet.stopSpin({
            symbols: spinInData,
            minDuration: window.clientAPI.regulatory.betLimit,
            anticipate: totalAnticipation,
        })

        if (hasAnticipated) {
            // Wait for tint tween to finish
            await flow.wait(200)
        }

        // Stop after slam stop
        flow.skipStop()
        connection?.disconnect()

        landConnection.disconnect()

        if (isTransforming) {
            // Destroy coin shower symbols
            this.views.coinShower.reset()
            // Replace results symbols with transform symbols
            reelSet.replaceSymbols(transformEvent.transforms)
        }

        if (!isBonusTrigger(model)) {
            await this.handleAllCollectionEvents()
        }

        await this.showAllLines(model)
        await this.handlePopup(model)

        const wins = model.results.wins.symbol.map((win) => ({
            lineInfo: win,
            lineId: win.line?.id || 0,
            winningAssets: this.views.reelSet.fetcher
                .getSymbolsByPosition(win.positions)
                .map((symbol) => symbol.spine)
                .filter((s) => s) as Spine[],
            nonWinningAssets: this.views.reelSet.fetcher
                .getSymbolsNotInPositions(win.positions)
                .map((symbol) => symbol.spine)
                .filter((s) => s) as Spine[],
        }))

        if (isBonusTrigger(model)) {
            // Animate the scatter symbols
            const positions = reelSet.fetcher.getPositions(valueIds)
            const symbols = reelSet.fetcher.getSymbolsByPosition(positions)
            reelSet.promoteSymbolsByPosition(sortByRowThenReel(positions))

            this.views.soundPlayer.playScatterTrigger()
            this.views.baseBackground.playScatter()
            const promises = Promise.all(symbols.map((symbol) => symbol.playWin()))

            if (!isTransforming) {
                // Empty
                await flow.wait(300)
                this.views.coinShower.animateEmpty()
                await flow.wait(2500)
            } else {
                await promises
            }
        } else {
            this.views.winLines.iterateLines(wins)
            this.isLoopingWinlines = true
        }
    }

    private async handleCoinShower(event: CoinShowerEvent) {
        const showerAnimationData = event.transforms.map((transform) => {
            const { position } = transform
            const destination = this.views.reelSet.getSymbolPosition(position[0], position[1])
            return { destination, transform }
        })
        await this.views.coinShower.animate(showerAnimationData)
    }

    /** animate all lines and symbols that have won */
    async showAllLines(model: AnyBaseGameModel) {
        this.views.idleHandler.setActive()
        if (model.state.phase.next === 'NORMAL') {
            // dont iterate if we are going to transition
            return
        }

        const winningPositions = model.results.wins.symbol.flatMap((win) => win.positions)
        const nonWinningSymbols = this.views.reelSet.fetcher.getSymbolsNotInPositions(winningPositions)
        const winningLines = model.results.wins.symbol.map((win) => win.line?.id).filter((id) => id !== undefined)
        const winningSymbols = this.views.reelSet.fetcher.getUniqueSymbolsByPosition(winningPositions)

        if (winningSymbols.length) {
            const winningAssets = winningSymbols.map((symbol) => symbol.spine).filter((s) => s) as Spine[]
            const nonWinningAssets = nonWinningSymbols.map((symbol) => symbol.spine).filter((s) => s) as Spine[]

            this.views.excitement.win()
            this.views.soundPlayer.playWin(winningSymbols.map((symbol) => symbol.id))
            await this.views.winLines.playOnce({
                winningAssets,
                nonWinningAssets,
                lineIds: winningLines,
                lineInfo: model.results.wins.symbol,
            })
        }
    }

    private async handleAllCollectionEvents() {
        const { eventManager } = this.views
        while (eventManager.peek()?.type === 'CASH_COLLECTION') {
            await this.handleCollectionEvent(eventManager.nextEvent() as CollectionEvent)
        }
    }

    private async handleCollectionEvent(event: CollectionEvent): Promise<void> {
        this.views.reelSet.promoteSymbolsByPosition(event.to)
        await this.views.collection.animate(this.views.reelSet.fetcher, event)
    }

    /** animate one of the popups based on win */
    async handlePopup(model: AnyBaseGameModel) {
        const bet = model.bet
        const win = model.state.win.total.currency
        const stake = bet.stake.currency

        // dont animate any popups if win below stake
        if (win >= stake) {
            if (win / stake >= 5) {
                await this.views.tieredPopup.animate(stake, win)
            } else {
                this.views.mediumWinPopup.animate(win)
                // bonus is triggering, lets wait for the popup to completely finish
                if (isBonusTrigger(model)) {
                    const mediumWinSpec = Specification.get('mediumWinPopup') as MediumWinPopupSpec
                    await flow.wait(mediumWinSpec.fadeDelay + mediumWinSpec.fadeDuration)
                }
            }
        }

        // show win
        if (win) {
            slotStore.getState().message.setMessage('returns', [formatCurrency(win)])
        }
    }

    /** round has errored, recover the spin and cancel autoplay */
    roundError(): void {
        this.views.reelSet.cancelSpin()
        slotStore.getState().spins.updateSpins(0)
    }
}
